import { HubConnectionBuilder, HubConnectionState, LogLevel, HttpError } from '@microsoft/signalr';
import { CommonConnectionsMethod, GameLiveConnectionMethod } from '../../constants/connectionTypes';
import { applicationStore } from '../../../store/applicationStore';
import { parseQuery } from '../../helpers/parseQuery';
import { ValidationType } from '../../constants/validationType';
import { userInfoStore } from '../../../store/userInfoStore';
import { notificationStore } from '../../../store/notificationStore';
import { NotificationsType } from '../../constants/notificationsType';
import { connectionPopupStore } from '../../../store/connectionPopupStore';
import { PopupTypes } from '../../constants/popupTypes';
import { launcherErrorCodes } from '../../constants/launcherErrorCodes';
import { handleErrorCodesPostMessage } from '../../helpers/handleErrorCodesPostMessage';
import { gambleStore } from '../../../store/gambleStore';

export class BaseLiveConnection {
  connection;

  connectionStartPromise;

  connectionStopPromise;

  url;

  isOnReconnectingState;

  constructor(url) {
    this.url = url;
  }

  async waitStarted() {
    if (this.connectionStartPromise instanceof Promise) {
      await this.connectionStartPromise;
      this.connectionStartPromise = null;
    }
  }

  async waitStop() {
    if (this.connectionStopPromise instanceof Promise) {
      await this.connectionStopPromise;
      this.connectionStopPromise = null;
    }
  }

  async init(reconnectTimes, skipNegotiation = false) {
    try {
      await this.waitStop();
      await this.waitStarted();
      return await this.connectionInitMiddleware(reconnectTimes, skipNegotiation);
    } catch (e) {
      this.connectionStartPromise = null;
      this.callbackMiddleware({
        methodName: CommonConnectionsMethod.ConnectionClose,
      });
      return Promise.reject(e);
    }
  }

  async connectionInitMiddleware(reconnectTimes, skipNegotiation = false) {
    if (this.connection?.state !== HubConnectionState.Connected) {
      this.connection = this.createConnection(reconnectTimes, skipNegotiation);
      this.connectionStartPromise = this.connection.start();
      await this.waitStarted();
    }
    return this.connection;
  }

  createConnection(reconnectTimes = [300, 15000]) {
    const { accessToken } = applicationStore;
    return new HubConnectionBuilder()
      .withUrl(this.url, { accessTokenFactory: () => accessToken })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect(reconnectTimes)
      .build();
  }

  async invoke(methodName, ...args) {
    await this.waitStarted();
    if (this.connection.state === HubConnectionState.Connected) {
      const { ConnectionError } = launcherErrorCodes;
      try {
        return this.connection.invoke(methodName, ...args).catch(() => {
          handleErrorCodesPostMessage(ConnectionError);
        });
      } catch (err) {
        handleErrorCodesPostMessage(ConnectionError);
      }
    }
    return null;
  }

  on(methodName, handler) {
    if (this.connection.state === HubConnectionState.Connected)
      this.connection.on(methodName, handler);
    return this;
  }

  onReconnecting(callback) {
    if (this.connection) {
      this.connection.onreconnecting((data) => {
        if (callback) {
          callback(data);
        }
        this.isOnReconnectingState = true;
        this.callbackMiddleware({
          methodName: CommonConnectionsMethod.Reconnecting,
        });
      });
    }
    return null;
  }

  onClose(callback) {
    if (this.connection && this.connection.state === HubConnectionState.Connected) {
      return this.connection.onclose((data) => {
        if (callback) {
          callback(data);
        }

        if (this.isOnReconnectingState) {
          this.callbackMiddleware({
            methodName: CommonConnectionsMethod.ConnectionClose,
          });
        }
      });
    }
    return null;
  }

  onReconnected(callback) {
    if (this.connection) {
      return this.connection.onreconnected((data) => {
        if (callback) {
          callback(data);
        }

        this.isOnReconnectingState = false;
        this.callbackMiddleware({
          methodName: CommonConnectionsMethod.Reconnected,
        });
      });
    }
    return null;
  }

  async stop() {
    await this.waitStarted();
    await this.waitStop();
    if (this.connection?.state === HubConnectionState.Connected) {
      this.connectionStopPromise = this.connection.stop().then(() => {
        this.connectionStartPromise = null;
      });
      await this.waitStop();
    }
  }

  callbackMiddleware({ data, methodName }) {
    const methods = {
      [GameLiveConnectionMethod.GetBalanceResult]: (arg) => {
        if (arg.validation === ValidationType.Success) {
          userInfoStore.setUserBalance(arg.balance);
        }
      },
      [GameLiveConnectionMethod.GetTournamentBalanceResult]: (arg) => {
        if (arg.validation === ValidationType.Success) {
          userInfoStore.setUserBalance(arg.balance);
        }
      },
      [GameLiveConnectionMethod.GameOpen]: (arg) => {
        const query = parseQuery(window.location.search);
        const { gameid } = query;
        if (this.connection?.connectionId !== arg.connectionId && arg.gameId === +gameid) {
          const { GameIsOpen } = launcherErrorCodes;
          this.stop();
          handleErrorCodesPostMessage(GameIsOpen);
        }
      },
      [CommonConnectionsMethod.ConnectionClose]: () => {
        const { ConnectionError } = launcherErrorCodes;
        handleErrorCodesPostMessage(ConnectionError);
      },
      [GameLiveConnectionMethod.Error]: () => {
        if (data instanceof HttpError) {
          gambleStore.resetGambleState();
          const { ConnectionError } = launcherErrorCodes;
          handleErrorCodesPostMessage(ConnectionError);
        }
      },
      [CommonConnectionsMethod.Reconnecting]: () => {
        gambleStore.resetGambleState();
        const { Reconnect } = launcherErrorCodes;
        handleErrorCodesPostMessage(Reconnect);
        connectionPopupStore.open(PopupTypes.RECONNECT_POPUP);
      },
      [CommonConnectionsMethod.Reconnected]: () => {
        gambleStore.resetGambleState();
        connectionPopupStore.close();
      },
      [CommonConnectionsMethod.UnderMaintenance]: (arg) => {
        notificationStore.showNotification({
          type: NotificationsType.Maintenance,
          notificationInfo: arg,
        });
      },
    };
    methods[methodName]?.(data);
  }
}
